import React from 'react';

const About = () => {
  const stats = [
    { number: '4', label: 'Projects Completed in 2025', icon: '🚀' },
    { number: '6+', label: 'Years Experience', icon: '⏱️' },
    { number: '100%', label: 'Client Satisfaction', icon: '⭐' },
    // { number: '$10M+', label: 'Client Funding Raised', icon: '💰' }
  ];

  const values = [
    {
      title: 'Innovation First',
      description: 'We leverage cutting-edge technologies and industry best practices to deliver solutions that give you a competitive edge.',
      icon: '💡',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Speed & Agility',
      description: 'Our streamlined process gets your MVP to market in weeks, not months. Fast iteration means faster success.',
      icon: '⚡',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Quality Assurance',
      description: 'Every line of code is crafted with scalability, security, and performance in mind. We build for the long term.',
      icon: '🛡️',
      gradient: 'from-green-500 to-teal-500'
    },
    {
      title: 'Client Success',
      description: 'Your success is our success. We provide ongoing support and guidance to help your business thrive.',
      icon: '🎯',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  const team = [
    {
      name: 'Farah Warfa',
      role: 'Founder & Lead Developer',
      bio: '5+ years building scalable web applications. Expert in React, Node.js, and cloud architecture.',
      avatar: '👨‍💻',
      skills: ['React', 'Next.js', 'Node.js', 'Tailwind CSS', 'PostgreSQL', 'AWS', 'System Design', 'Product Strategy', 'UX/UI & Business Strategy', 'Analytics', 'Conversion Optimization']
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-1000"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
              🏢 About Tech Talented
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Building the Future, One Project at a Time
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We're a boutique software consultancy that specializes in transforming startup ideas into market-ready products. 
            From concept to launch, we're your technical co-founders.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mb-20">
          {stats.map((stat, index) => (
            <div key={index} className="text-center group">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group-hover:-translate-y-2">
                <div className="text-4xl mb-3">{stat.icon}</div>
                <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Our Story */}
        <div className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h3>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                <p>
                  Founded with a mission to bridge the gap between ambitious startup ideas and technical execution, 
                  Tech Talented has been helping entrepreneurs bring their visions to life since day one.
                </p>
                <p>
                  We've seen too many great ideas fail due to poor technical implementation or endless development cycles. 
                  That's why we focus on rapid MVP development and conversion-optimized websites that actually drive results.
                </p>
                <p>
                  Our approach combines startup speed with enterprise-grade quality, ensuring your product not only 
                  launches quickly but scales as your business grows.
                </p>
              </div>
              <div className="mt-8">
                <button
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                >
                  Work With Us
                </button>
              </div>
            </div>
            
            {/* Visual Element */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-8 text-white">
                <h4 className="text-2xl font-bold mb-6">Why Choose Us?</h4>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>100% client satisfaction to date</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Average project delivery in 6-8 weeks</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Post-launch support and scaling guidance</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span>Transparent communication throughout</span>
                  </div>
                </div>
              </div>
              
              {/* Floating Element */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-yellow-400 rounded-2xl flex items-center justify-center shadow-xl transform rotate-12 animate-bounce">
                <span className="text-3xl">🏆</span>
              </div>
            </div>
          </div>
        </div>

        {/* Our Values */}
        {/* <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Values</h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide every project we deliver
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div key={index} className="group">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 h-full group-hover:-translate-y-2">
                  <div className={`w-16 h-16 bg-gradient-to-br ${value.gradient} rounded-2xl flex items-center justify-center text-2xl text-white mb-6 group-hover:scale-110 transition-transform`}>
                    {value.icon}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div> */}

        {/* Team */}
        {/* <div className="text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Meet the Team</h3>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            Experienced professionals dedicated to your success
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="text-6xl mb-4">{member.avatar}</div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h4>
                <p className="text-blue-600 font-semibold mb-4">{member.role}</p>
                <p className="text-gray-600 mb-6 leading-relaxed">{member.bio}</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {member.skills.map((skill, skillIndex) => (
                    <span key={skillIndex} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default About;

